import React, { useState, useRef, useEffect } from 'react';

function App() {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  const [latency, setLatency] = useState(null);
  const [isCached, setIsCached] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);

  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const chatEndRef = useRef(null);
  const ttsAbortRef = useRef(null);

  // Audio playback detection
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsSpeaking(true);
    const handleEnded = () => setIsSpeaking(false);
    const handlePause = () => setIsSpeaking(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  const startRecording = async () => {
    try {
      setError('');
      setHasInteracted(true);

      // Unlock audio autoplay while we still have the user gesture
      const audio = audioRef.current;
      if (audio) {
        audio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
        await audio.play().catch(() => {});
        audio.pause();
        audio.removeAttribute('src');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await sendAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('🎤 Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setError('🎤 No microphone found. Please connect a microphone and try again.');
      } else {
        setError('🎤 Microphone error: ' + err.message);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true);
    }
  };

  const sendAudio = async (audioBlob) => {
    try {
      const base64Audio = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });

      const apiUrl = window.location.hostname === 'localhost'
        ? 'http://localhost:3001/api/voice-chat'
        : '/api/voice-chat';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audio: base64Audio }),
      });

      if (!response.ok) {
        throw new Error('Network error. Please check your connection.');
      }

      const data = await response.json();

      if (data.error) {
        setError('❌ ' + data.error);
      } else {
        setTranscript(data.transcript);
        setLatency(data.latency);
        setIsCached(data.cached);
        streamAudio(data.transcript);
      }
    } catch (err) {
      setError('🌐 Connection error: ' + err.message + '. Please ensure the server is running.');
    } finally {
      setIsProcessing(false);
    }
  };

  const sendChat = async (e) => {
    e?.preventDefault();
    const text = chatInput.trim();
    if (!text || isChatLoading) return;

    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text }]);
    setIsChatLoading(true);

    const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
    try {
      const response = await fetch(`${apiBase}/api/text-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const data = await response.json();
      const answer = data.transcript || data.error || 'Something went wrong.';
      setChatMessages(prev => [...prev, { role: 'assistant', text: answer, cached: data.cached }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', text: 'Connection error: ' + err.message }]);
    } finally {
      setIsChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  const handleSuggestionClick = (suggestionText) => {
    setHasInteracted(true);
    // Simulate voice input for demo
    alert(`In production, this would trigger voice recording for: "${suggestionText}"`);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(transcript);
    // Show temporary feedback
    const btn = document.getElementById('copy-btn');
    if (btn) {
      const original = btn.textContent;
      btn.textContent = '✓ Copied';
      setTimeout(() => btn.textContent = original, 2000);
    }
  };

  const testAudio = () => {
    if (audioRef.current && audioRef.current.src) {
      audioRef.current.play().catch(err => {
        console.error('Audio playback failed:', err);
        setError('🔊 Audio playback failed: ' + err.message);
      });
    } else {
      setError('🔊 No audio available to play. Record a question first.');
    }
  };

  const streamAudio = async (text) => {
    // Cancel any in-flight TTS request
    if (ttsAbortRef.current) {
      ttsAbortRef.current.abort();
    }
    const controller = new AbortController();
    ttsAbortRef.current = controller;

    const apiBase = window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : '';

    try {
      const response = await fetch(`${apiBase}/api/tts-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
        signal: controller.signal,
      });

      if (!response.ok) throw new Error('TTS stream failed');

      const audio = audioRef.current;
      if (!audio) return;

      const waitForUpdate = (sb) =>
        new Promise((r) => sb.addEventListener('updateend', r, { once: true }));

      if ('MediaSource' in window && MediaSource.isTypeSupported('audio/mpeg')) {
        const mediaSource = new MediaSource();
        const objectUrl = URL.createObjectURL(mediaSource);
        audio.src = objectUrl;

        mediaSource.addEventListener('sourceopen', async () => {
          URL.revokeObjectURL(objectUrl);
          const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
          const reader = response.body.getReader();
          let started = false;

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                if (sourceBuffer.updating) await waitForUpdate(sourceBuffer);
                mediaSource.endOfStream();
                break;
              }
              if (sourceBuffer.updating) await waitForUpdate(sourceBuffer);
              sourceBuffer.appendBuffer(value);
              if (!started) {
                started = true;
                audio.play().catch(console.error);
              }
            }
          } catch (err) {
            if (err.name !== 'AbortError') console.error('Stream read error:', err);
          }
        }, { once: true });
      } else {
        // Fallback: buffer all chunks then play
        const chunks = [];
        const reader = response.body.getReader();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
        }
        const blob = new Blob(chunks, { type: 'audio/mpeg' });
        const blobUrl = URL.createObjectURL(blob);
        audio.src = blobUrl;
        audio.addEventListener('ended', () => URL.revokeObjectURL(blobUrl), { once: true });
        audio.play();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('TTS streaming error:', err);
        setError('🔊 Audio playback failed: ' + err.message);
      }
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#0a0a0f',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <style>{`
        @keyframes pulse-ring {
          0% {
            transform: scale(0.95);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.7;
          }
          100% {
            transform: scale(0.95);
            opacity: 1;
          }
        }
        @keyframes wave {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.5); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(6, 182, 212, 0.3), 0 0 40px rgba(6, 182, 212, 0.1); }
          50% { box-shadow: 0 0 30px rgba(6, 182, 212, 0.5), 0 0 60px rgba(6, 182, 212, 0.2); }
        }
        .suggestion-chip {
          padding: 10px 16px;
          background: rgba(30, 41, 59, 0.8);
          border: 1px solid rgba(100, 116, 139, 0.3);
          border-radius: 20px;
          color: #cbd5e1;
          font-size: 13px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .suggestion-chip:hover {
          background: rgba(51, 65, 85, 0.9);
          border-color: rgba(6, 182, 212, 0.5);
          transform: translateY(-2px);
        }
        .speaking-bar {
          display: inline-block;
          width: 3px;
          height: 12px;
          background: #06b6d4;
          margin: 0 2px;
          border-radius: 2px;
          animation: wave 0.8s ease-in-out infinite;
        }
        .speaking-bar:nth-child(2) { animation-delay: 0.1s; }
        .speaking-bar:nth-child(3) { animation-delay: 0.2s; }
        .speaking-bar:nth-child(4) { animation-delay: 0.3s; }
      `}</style>

      <div style={{
        background: 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(100, 116, 139, 0.2)',
        borderRadius: '24px',
        padding: '48px',
        maxWidth: '700px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        animation: 'fade-in 0.5s ease-out'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'inline-block',
            padding: '6px 16px',
            background: 'rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.3)',
            borderRadius: '20px',
            marginBottom: '16px'
          }}>
            <span style={{ color: '#06b6d4', fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px' }}>
              JARVIS RESUME
            </span>
          </div>

          <h1 style={{
            color: '#f1f5f9',
            fontSize: '32px',
            fontWeight: '700',
            margin: '0 0 8px 0',
            letterSpacing: '-0.5px'
          }}>
            Lavanya Karan
          </h1>

          <p style={{
            color: '#94a3b8',
            fontSize: '16px',
            margin: 0
          }}>
            Ask anything about my experience
          </p>
        </div>

        {/* Microphone Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '40px',
          position: 'relative'
        }}>
          {/* Pulse ring animation when recording */}
          {isRecording && (
            <div style={{
              position: 'absolute',
              width: '140px',
              height: '140px',
              borderRadius: '50%',
              border: '2px solid rgba(239, 68, 68, 0.5)',
              animation: 'pulse-ring 1.5s ease-out infinite',
              pointerEvents: 'none'
            }} />
          )}

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isProcessing}
              style={{
                width: '130px',
                height: '130px',
                borderRadius: '50%',
                border: isRecording ? '3px solid #ef4444' : '3px solid rgba(6, 182, 212, 0.4)',
                background: isRecording
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : isProcessing
                  ? 'linear-gradient(135deg, #475569 0%, #334155 100%)'
                  : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: 'white',
                fontSize: '48px',
                fontWeight: 'bold',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                boxShadow: isRecording
                  ? '0 0 30px rgba(239, 68, 68, 0.4)'
                  : '0 0 30px rgba(6, 182, 212, 0.3)',
                transition: 'all 0.3s ease',
                opacity: isProcessing ? 0.6 : 1,
                position: 'relative',
                animation: isRecording ? 'glow-pulse 2s infinite' : 'none'
              }}
              onMouseOver={(e) => {
                if (!isProcessing && !isRecording) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 0 40px rgba(6, 182, 212, 0.5)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = isRecording
                  ? '0 0 30px rgba(239, 68, 68, 0.4)'
                  : '0 0 30px rgba(6, 182, 212, 0.3)';
              }}
            >
              {isProcessing ? '⏳' : isRecording ? '🎤' : '🎤'}
            </button>

            <p style={{
              marginTop: '16px',
              color: isRecording ? '#ef4444' : isProcessing ? '#94a3b8' : '#06b6d4',
              fontSize: '14px',
              fontWeight: '600',
              letterSpacing: '0.3px'
            }}>
              {isProcessing ? 'Processing...' : isRecording ? 'Recording • Tap to stop' : 'Tap to ask about experience'}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            color: '#fca5a5',
            fontSize: '14px',
            lineHeight: '1.6',
            animation: 'fade-in 0.3s ease-out'
          }}>
            {error}
          </div>
        )}

        {/* Processing State */}
        {isProcessing && !error && (
          <div style={{
            background: 'rgba(100, 116, 139, 0.1)',
            border: '1px solid rgba(100, 116, 139, 0.2)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            animation: 'fade-in 0.3s ease-out'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: '#94a3b8',
              fontSize: '14px'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderTop: '2px solid #06b6d4',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <span>Generating answer...</span>
            </div>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {/* Speaking Indicator */}
        {isSpeaking && (
          <div style={{
            background: 'rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.3)',
            borderRadius: '12px',
            padding: '12px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'fade-in 0.3s ease-out'
          }}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <span className="speaking-bar"></span>
              <span className="speaking-bar"></span>
              <span className="speaking-bar"></span>
              <span className="speaking-bar"></span>
            </div>
            <span style={{ color: '#06b6d4', fontSize: '14px', fontWeight: '600' }}>
              JARVIS speaking...
            </span>
          </div>
        )}

        {/* Answer */}
        {transcript && (
          <div style={{
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid rgba(100, 116, 139, 0.3)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            animation: 'fade-in 0.4s ease-out'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <h3 style={{
                margin: 0,
                color: '#e2e8f0',
                fontSize: '16px',
                fontWeight: '600',
                letterSpacing: '0.3px'
              }}>
                Response
              </h3>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  id="copy-btn"
                  onClick={copyToClipboard}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(100, 116, 139, 0.3)',
                    border: '1px solid rgba(100, 116, 139, 0.4)',
                    borderRadius: '8px',
                    color: '#cbd5e1',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'rgba(100, 116, 139, 0.5)';
                    e.target.style.borderColor = 'rgba(6, 182, 212, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(100, 116, 139, 0.3)';
                    e.target.style.borderColor = 'rgba(100, 116, 139, 0.4)';
                  }}
                >
                  📋 Copy
                </button>
                <button
                  onClick={testAudio}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(100, 116, 139, 0.3)',
                    border: '1px solid rgba(100, 116, 139, 0.4)',
                    borderRadius: '8px',
                    color: '#cbd5e1',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.background = 'rgba(100, 116, 139, 0.5)';
                    e.target.style.borderColor = 'rgba(6, 182, 212, 0.5)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = 'rgba(100, 116, 139, 0.3)';
                    e.target.style.borderColor = 'rgba(100, 116, 139, 0.4)';
                  }}
                >
                  🔊 Replay
                </button>
              </div>
            </div>

            <p style={{
              lineHeight: '1.8',
              color: '#cbd5e1',
              fontSize: '15px',
              margin: '0 0 16px 0',
              whiteSpace: 'pre-wrap'
            }}>
              {transcript}
            </p>

            <div style={{
              display: 'flex',
              gap: '16px',
              fontSize: '13px',
              color: '#64748b',
              paddingTop: '16px',
              borderTop: '1px solid rgba(100, 116, 139, 0.2)'
            }}>
              <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                ⚡ {latency}ms
              </span>
              {isCached && (
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color: '#06b6d4'
                }}>
                  💾 Cached
                </span>
              )}
            </div>
          </div>
        )}

        {/* Welcome Message and Suggestion Chips (Show when empty) */}
        {!hasInteracted && !transcript && (
          <>
            {/* Welcome Introduction */}
            <div style={{
              background: 'rgba(6, 182, 212, 0.05)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '32px',
              animation: 'fade-in 0.5s ease-out 0.2s both'
            }}>
              <p style={{
                color: '#cbd5e1',
                fontSize: '15px',
                lineHeight: '1.8',
                margin: 0,
                textAlign: 'center'
              }}>
                Hi, I'm <strong style={{ color: '#06b6d4' }}>Lavanya Karan</strong>. You can ask me anything about my experience, projects, or product work. Tap the microphone to ask your question, then tap it again when you're done speaking.
              </p>
            </div>

            {/* Suggestion Chips */}
            <div style={{
              marginBottom: '32px',
              animation: 'fade-in 0.5s ease-out 0.3s both'
            }}>
              <p style={{
                textAlign: 'center',
                color: '#64748b',
                fontSize: '13px',
                marginBottom: '16px',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                Try asking
              </p>

            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              justifyContent: 'center'
            }}>
              {[
                'Tell me about yourself',
                'What product have you built so far',
                'Describe your recent role',
                'What are your strengths?'
              ].map((suggestion, idx) => (
                <button
                  key={idx}
                  className="suggestion-chip"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
          </>
        )}

        {/* Example Questions (Always visible at bottom) */}
        <div style={{
          textAlign: 'center',
          paddingTop: '24px',
          borderTop: '1px solid rgba(100, 116, 139, 0.2)'
        }}>
          <p style={{
            color: '#475569',
            fontSize: '12px',
            margin: '0 0 8px 0',
            fontWeight: '600',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            Example Questions
          </p>
          <p style={{
            fontSize: '13px',
            lineHeight: '1.8',
            color: '#64748b',
            margin: 0
          }}>
            "What's your AI experience?" • "What industries or domains you have worked in" • "Describe a major project"
          </p>
        </div>
      </div>

      {/* Floating chat button */}
      <button
        onClick={() => setIsChatOpen(o => !o)}
        style={{
          position: 'fixed',
          bottom: '28px',
          right: '28px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: isChatOpen ? '#0891b2' : 'linear-gradient(135deg, #06b6d4, #0891b2)',
          border: 'none',
          boxShadow: '0 4px 20px rgba(6, 182, 212, 0.4)',
          color: '#fff',
          fontSize: '22px',
          cursor: 'pointer',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
        }}
        title="Text Chat"
      >
        {isChatOpen ? '✕' : '💬'}
      </button>

      {/* Floating chat panel */}
      {isChatOpen && (
        <div style={{
          position: 'fixed',
          bottom: '96px',
          right: '28px',
          width: '360px',
          height: '480px',
          background: 'rgba(15, 23, 42, 0.97)',
          border: '1px solid rgba(100, 116, 139, 0.25)',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          zIndex: 999,
          animation: 'fade-in 0.25s ease-out',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 18px',
            borderBottom: '1px solid rgba(100, 116, 139, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            background: 'rgba(6, 182, 212, 0.08)',
          }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: '#06b6d4', boxShadow: '0 0 6px #06b6d4'
            }} />
            <span style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '14px' }}>
              Chat with JARVIS
            </span>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}>
            {chatMessages.length === 0 && (
              <p style={{ color: '#475569', fontSize: '13px', textAlign: 'center', marginTop: '60px' }}>
                Ask anything about my experience
              </p>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: msg.role === 'user'
                    ? 'linear-gradient(135deg, #06b6d4, #0891b2)'
                    : 'rgba(30, 41, 59, 0.9)',
                  border: msg.role === 'assistant' ? '1px solid rgba(100, 116, 139, 0.2)' : 'none',
                  color: msg.role === 'user' ? '#fff' : '#cbd5e1',
                  fontSize: '13px',
                  lineHeight: '1.6',
                }}>
                  {msg.text}
                  {msg.cached && (
                    <span style={{ display: 'block', fontSize: '11px', color: '#06b6d4', marginTop: '4px' }}>💾 cached</span>
                  )}
                </div>
              </div>
            ))}
            {isChatLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '16px 16px 16px 4px',
                  background: 'rgba(30, 41, 59, 0.9)',
                  border: '1px solid rgba(100, 116, 139, 0.2)',
                  color: '#64748b',
                  fontSize: '13px',
                }}>
                  Thinking...
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendChat} style={{
            display: 'flex',
            gap: '8px',
            padding: '12px',
            borderTop: '1px solid rgba(100, 116, 139, 0.15)',
          }}>
            <input
              type="text"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              placeholder="Ask about my experience..."
              autoFocus
              style={{
                flex: 1,
                padding: '10px 14px',
                background: 'rgba(30, 41, 59, 0.8)',
                border: '1px solid rgba(100, 116, 139, 0.3)',
                borderRadius: '10px',
                color: '#e2e8f0',
                fontSize: '13px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={isChatLoading || !chatInput.trim()}
              style={{
                padding: '10px 14px',
                background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '13px',
                fontWeight: '600',
                cursor: isChatLoading || !chatInput.trim() ? 'not-allowed' : 'pointer',
                opacity: isChatLoading || !chatInput.trim() ? 0.5 : 1,
              }}
            >
              Send
            </button>
          </form>
        </div>
      )}

      <audio ref={audioRef} style={{ display: 'none' }} />
    </div>
  );
}

export default App;
